import { Context } from '../context/Context';
import { Camera } from '../camera';
import { FullscreenQuad } from './FullscreenQuad';
import { GBuffer } from './GBuffer';
import { RenderTexture, createColorTexture, createFramebuffer } from './RenderTarget';
import { DirectionalLight } from '../light';
import { vec3 } from 'gl-matrix';

export type VolumetricQuality = 'low' | 'medium' | 'high';

const QUALITY_SETTINGS: Record<
  VolumetricQuality,
  { resolutionDivisor: number; stepCount: number }
> = {
  low: { resolutionDivisor: 4, stepCount: 16 },
  medium: { resolutionDivisor: 2, stepCount: 24 },
  high: { resolutionDivisor: 2, stepCount: 32 },
};

export class VolumetricPass {
  private volumetricFBO!: WebGLFramebuffer;
  private blurFBO!: WebGLFramebuffer;
  private volumetricTexture!: RenderTexture;
  private blurTexture!: RenderTexture;
  private width!: number;
  private height!: number;
  private frameCounter = 0;
  private currentQuality: VolumetricQuality = 'medium';

  init(ctx: Context, quality: VolumetricQuality = 'medium'): void {
    this.currentQuality = quality;
    this.createFBOs(ctx);
  }

  setQuality(ctx: Context, quality: VolumetricQuality): void {
    if (quality === this.currentQuality) return;
    this.currentQuality = quality;
    // Recreate FBOs at new resolution
    const gl = ctx.gl;
    if (this.volumetricFBO) gl.deleteFramebuffer(this.volumetricFBO);
    if (this.blurFBO) gl.deleteFramebuffer(this.blurFBO);
    if (this.volumetricTexture?.glTexture) gl.deleteTexture(this.volumetricTexture.glTexture);
    if (this.blurTexture?.glTexture) gl.deleteTexture(this.blurTexture.glTexture);
    this.createFBOs(ctx);
  }

  private createFBOs(ctx: Context): void {
    const gl = ctx.gl;
    const divisor = QUALITY_SETTINGS[this.currentQuality].resolutionDivisor;
    this.width = Math.floor(ctx.viewPort.width / divisor);
    this.height = Math.floor(ctx.viewPort.height / divisor);

    this.volumetricTexture = new RenderTexture();
    this.volumetricTexture.glTexture = createColorTexture(
      gl,
      this.width,
      this.height,
      gl.RGBA16F,
      gl.RGBA,
      gl.FLOAT,
    );
    this.volumetricFBO = createFramebuffer(gl, this.volumetricTexture.glTexture!);

    this.blurTexture = new RenderTexture();
    this.blurTexture.glTexture = createColorTexture(
      gl,
      this.width,
      this.height,
      gl.RGBA16F,
      gl.RGBA,
      gl.FLOAT,
    );
    this.blurFBO = createFramebuffer(gl, this.blurTexture.glTexture!);
  }

  get resultTexture(): RenderTexture {
    return this.volumetricTexture;
  }

  render(
    ctx: Context,
    gBuffer: GBuffer,
    quad: FullscreenQuad,
    camera: Camera,
    sunLight: DirectionalLight,
    outputBuffer: WebGLFramebuffer | null,
  ): void {
    const gl = ctx.gl;
    const csm = sunLight.cascadedShadowMap;
    if (!csm) return;

    const settings = QUALITY_SETTINGS[this.currentQuality];
    this.frameCounter++;

    // === 1. Volumetric ray march ===
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.volumetricFBO);
    gl.viewport(0, 0, this.width, this.height);
    gl.clear(gl.COLOR_BUFFER_BIT);

    const shader = ctx.engine.shaderManager.getVolumetricShader();
    ctx.setShader(shader);

    shader.uniformsContainer
      .setDepthMap(gBuffer.depthTexture)
      .setInvViewProjection(camera.invViewProjection)
      .setShadowCascadeMap0(csm.cascadeTextures[0])
      .setShadowCascadeMap1(csm.cascadeTextures[1])
      .setShadowCascadeMap2(csm.cascadeTextures[2])
      .setShadowCascadeMap3(csm.cascadeTextures[3])
      .setCascadeViewMatrix(0, csm.cascadeViewMatrices[0])
      .setCascadeViewMatrix(1, csm.cascadeViewMatrices[1])
      .setCascadeViewMatrix(2, csm.cascadeViewMatrices[2])
      .setCascadeViewMatrix(3, csm.cascadeViewMatrices[3])
      .setCascadeSplits(csm.cascadeSplits)
      .setViewMatrix(camera.modelViewMatrix)
      .setCameraPosition(camera.cameraPosition as vec3)
      .setSunDirection(sunLight.direction as number[])
      .setSunColor(sunLight.color as number[])
      .setFogDensity(sunLight.volumetricDensity)
      .setScatteringCoeff(sunLight.volumetricScattering)
      .setStepCount(settings.stepCount)
      .setFrameJitter(this.frameCounter * 1.618033); // golden ratio for good distribution

    shader.validate();
    quad.draw(gl);

    // === 2. Blur pass (reuse SSAO blur shader for simplicity) ===
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.blurFBO);
    gl.clear(gl.COLOR_BUFFER_BIT);

    const blurShader = ctx.engine.shaderManager.getSsaoBlurShader();
    ctx.setShader(blurShader);

    blurShader.uniformsContainer
      .setSsaoMap(this.volumetricTexture)
      .setDepthMap(gBuffer.depthTexture)
      .setInvProjection(camera.invProjection)
      .setDirection([1.0, 0.0])
      .setTexelSize([1.0 / this.width, 1.0 / this.height]);

    blurShader.validate();
    quad.draw(gl);

    // Vertical blur back to volumetric texture
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.volumetricFBO);
    gl.clear(gl.COLOR_BUFFER_BIT);

    ctx.setShader(blurShader);

    blurShader.uniformsContainer
      .setSsaoMap(this.blurTexture)
      .setDepthMap(gBuffer.depthTexture)
      .setInvProjection(camera.invProjection)
      .setDirection([0.0, 1.0])
      .setTexelSize([1.0 / this.width, 1.0 / this.height]);

    blurShader.validate();
    quad.draw(gl);

    // === 3. Composite onto screen (additive blend) ===
    gl.bindFramebuffer(gl.FRAMEBUFFER, outputBuffer);
    gl.viewport(0, 0, ctx.viewPort.width, ctx.viewPort.height);

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE);
    gl.disable(gl.DEPTH_TEST);

    const compositeShader = ctx.engine.shaderManager.getFullscreenCompositeShader();
    ctx.setShader(compositeShader);

    compositeShader.uniformsContainer.setTextureSampler(this.volumetricTexture);

    compositeShader.validate();
    quad.draw(gl);

    gl.disable(gl.BLEND);
  }

  destroy(gl: WebGL2RenderingContext): void {
    gl.deleteFramebuffer(this.volumetricFBO);
    gl.deleteFramebuffer(this.blurFBO);
    if (this.volumetricTexture?.glTexture) gl.deleteTexture(this.volumetricTexture.glTexture);
    if (this.blurTexture?.glTexture) gl.deleteTexture(this.blurTexture.glTexture);
  }
}
